import { useMutation, useQuery } from "@apollo/client/react";

import { graphql } from "../../../__generated__/gql";
import { CancelAccommodationReservationErrorCase } from "../../../__generated__/gql/graphql";
import { LEGAL_DOCUMENTS } from "../../../constants";
import { useSnackbar } from "../../_contexts/snackbar";
import { useAsyncAction } from "../../_hooks/use-async-action";
import { useInNativeApp } from "../../_hooks/use-in-native-app";
import { postMessageToNativeApp } from "../../utils/native-app";
import { CancelFeePolicyArea } from "../reservation-form/cancel-fee-policy-area";
import { Link } from "../ui/link";

import { ChangeOrCancelFee } from "./change-or-cancel-fee";
import { ReservationSummary } from "./reservation-summary";

const Query = graphql(`
  query AccommodationReservationCancelFee($id: ID!, $internal: Boolean!) {
    estimatedCancelFeeOfAccommodationReservation(id: $id, internal: $internal) {
      ... on EstimatedChargeOfAccommodationReservation {
        items {
          __typename
        }
      }
      ...ChangeOrCancelEstimatedChargeFragment
    }
    accommodationReservation(id: $id) {
      accommodation {
        name
      }
      reservedFrom
      reservedTo
      provider {
        __typename
      }
      detail {
        numberOfAdults
        numberOfTotalChildren
        previousLocation
        nextLocation
      }
      stayTicketUnit {
        ...StayTicketUnitForCancelFeePolicyFragment
      }
      ...ReservationSummary
    }
  }
`);

const Mutation = graphql(`
  mutation CancelAccommodationReservation($id: ID!) {
    cancelAccommodationReservation(id: $id) {
      ... on AccommodationReservation {
        id
      }
      ... on CancelAccommodationReservationError {
        case
      }
    }
  }
`);

const InternalMutation = graphql(`
  mutation InternalCancelAccommodationReservation($id: ID!) {
    internalCancelAccommodationReservation(id: $id) {
      ... on AccommodationReservation {
        id
      }
      ... on CancelAccommodationReservationError {
        case
      }
    }
  }
`);

export function CancelReservationContent({
  reservationId,
  isInternal = false,
  skip = false,
  onComplete,
  onCancelClick,
  cancelButtonLabel = "キャンセルしない",
}: {
  reservationId: string;
  isInternal?: boolean;
  /** クエリの実行をスキップする（ダイアログが閉じている時など） */
  skip?: boolean;
  onComplete: () => void;
  /** 「キャンセルしない」ボタン押下時のハンドラ。未指定ならボタンを表示しない */
  onCancelClick?: () => void;
  cancelButtonLabel?: string;
}) {
  const { data } = useQuery(Query, {
    variables: {
      id: reservationId,
      internal: isInternal,
    },
    skip,
  });

  const [cancelReservation, { loading: cancelReservationLoading }] =
    useMutation(Mutation);
  const [
    internalCancelReservation,
    { loading: internalCancelReservationLoading },
  ] = useMutation(InternalMutation);

  const estimation = data?.estimatedCancelFeeOfAccommodationReservation;
  const reservation = data?.accommodationReservation;

  const showSnackbar = useSnackbar();
  const isInNativeApp = useInNativeApp();

  const { isPending, execute } = useAsyncAction();
  const handleCancel = async () => {
    await execute(async () => {
      let result;
      if (isInternal) {
        const { data } = await internalCancelReservation({
          variables: {
            id: reservationId,
          },
        });
        result = data?.internalCancelAccommodationReservation;
      } else {
        const { data } = await cancelReservation({
          variables: {
            id: reservationId,
          },
        });
        result = data?.cancelAccommodationReservation;
      }

      switch (result?.__typename) {
        case "AccommodationReservation":
          if (isInNativeApp) {
            postMessageToNativeApp(
              {
                type: "ReservationCancel",
                payload: { reservationId: result.id },
              },
              window,
            );
          } else {
            showSnackbar({ message: "予約がキャンセルされました" });
          }
          onComplete();
          break;
        case "CancelAccommodationReservationError":
          // eslint-disable-next-line no-console
          console.error("ErrorCase:", result.case);
          if (
            result.case ===
            CancelAccommodationReservationErrorCase.HasActiveFacilityReservation
          ) {
            // 設備予約が残っているケースは WebView/ブラウザ問わず、
            // 具体的な解決手順を案内するため alert を維持
            alert(
              "設備予約があるため、宿泊予約をキャンセルできません。先に設備予約をキャンセルしてください。",
            );
          } else if (isInNativeApp) {
            // nativeapp 経由の場合は postMessage で native 側に通知し、
            // 表示は nativeapp の usePostMessageWebView がエラートーストで行う。
            // alert を出すと nativeapp のトーストと重複するためスキップ。
            postMessageToNativeApp(
              {
                type: "ReservationCancel",
                payload: { case: "GeneralError" },
              },
              window,
            );
          } else {
            alert("予約のキャンセルに失敗しました");
          }
          break;
      }
    });
  };

  if (
    !estimation ||
    estimation.__typename !== "EstimatedChargeOfAccommodationReservation" ||
    !reservation
  ) {
    return null;
  }

  return (
    <div className="text-skogecha flex flex-col">
      <div className="text-[13px] leading-[1.8] font-medium">
        この予約をキャンセルします。
        <br />
        ご確認の上、「予約をキャンセルする」ボタンを押してください。
      </div>
      <div className="mt-[14px] w-fit">
        {reservation.stayTicketUnit ||
        reservation.provider.__typename ===
          "OnlineTravelAgentAccommodationReservationProvider" ? (
          <div className="text-[12px]">
            <CancelFeePolicyArea
              stayTicketUnit={reservation.stayTicketUnit}
              cannotChangeReservationNoticeHidden={true}
            />
          </div>
        ) : (
          <div className="border-b-sorange border-b pb-[8px] text-[12px] leading-[1.1] font-bold">
            <Link
              data-analytics-id="reservation_cancel_clicked_cancel_fee_policy"
              href={
                reservation?.provider.__typename ===
                "CoOwnersPlanAccommodationReservationProvider"
                  ? LEGAL_DOCUMENTS.cancelFeePolicyOfCoOwners.url
                  : LEGAL_DOCUMENTS.cancelFeePolicy.url
              }
              target="_blank"
            >
              キャンセル料金のルールについて
            </Link>
          </div>
        )}
      </div>
      <div className="mt-[42px] mb-[20px] text-[12px] leading-none font-bold">
        キャンセルされる宿泊予約
      </div>
      <div className="md:w-[280px]">
        <ReservationSummary reservationData={reservation} />
      </div>
      <div className="mt-[40px]"></div>
      {estimation.items.length > 0 && (
        <ChangeOrCancelFee estimationData={estimation} />
      )}
      <div className="mt-[4px] mb-[23px] text-[12px] leading-none font-medium">
        {estimation.items.length > 0 ? (
          <>キャンセル料金の決済後に、予約がキャンセルされます。</>
        ) : (
          <>キャンセル料金はかかりません。</>
        )}
      </div>
      <div className="mt-[40px] flex flex-col gap-[10px] text-[12px] md:flex-row md:gap-[20px]">
        <button
          data-analytics-id="reservation_cancel_submitted_cancel"
          onClick={handleCancel}
          className="btn-primary md:w-[420px]"
          disabled={
            cancelReservationLoading ||
            internalCancelReservationLoading ||
            isPending
          }
        >
          予約をキャンセルする
        </button>
        {onCancelClick && (
          <button
            data-analytics-id="reservation_cancel_dismissed_cancel"
            type="button"
            onClick={onCancelClick}
            className="btn-secondary md:w-[190px]"
            disabled={isPending}
          >
            {cancelButtonLabel}
          </button>
        )}
      </div>
    </div>
  );
}
